import React from 'react';
import styled from '@emotion/styled';

export const Button = styled.button`
  padding: 8px 15px;
  border-radius: 4px;
  text-transform: uppercase;
  background-color: #21d36e;
  border: none;
  box-shadow: 0px 3px 5px -3px grey;
  color: white;
  &:hover {
    box-shadow: 0px 5px 8px -2px gray;
    cursor: pointer;
  }
  &:disabled {
    background-color: #bbb;
  }
  &:disabled:hover {
    box-shadow: 0px 3px 5px -3px grey;
    cursor: not-allowed;
  }
`;

export const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ErrorMessage = styled.div`
  color: red;
  font-size: 14px;
  padding-top: 8px;
  text-align: center;
`;

export const FieldsContainer = styled.div`
  background: white;
  padding: 15px;
  border-radius: 25px;
  box-shadow: 0px 2px 5px lightgray;
`;

export const Input = styled.input`
  height: 1.18em;
  padding: 6px 3px;
  margin: 0;
  border: 0;
  display: block;
  min-width: 0;
  background: none;
  box-sizing: content-box;
  border-radius: 0px;
  border-bottom: 1px solid lightgray;
  border-top-style: hidden;
  border-left-style: hidden;
  border-right-style: hidden;
  -webkit-tap-highlight-color: transparent;
  line-height: 1.5;
  &:focus {
    outline: none;
    border-bottom: 2px solid #21d36e;
  }
`;

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;
`;

export const InputLabel = styled.label`
  color: #666;
  font-size: 12px;
`;

export interface FormField extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** If using a third-party input component.
   * For example, phone number input w/ special
   * formatting and validation. */
  customInput?: React.ReactNode;
  errorMessage?: string;
}

export interface FormProps {
  fields: FormField[];
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  submitText?: string;
  disabled?: boolean;
  errorMessage?: string;
  renderAfterFields?: React.ReactNode;
  containerStyle?: React.CSSProperties;
  buttonProps?: React.HTMLProps<HTMLButtonElement>;
}

/** Simple Form Component. Does NOT handle validation. Consumer
 * is responsible for validating input and passing error message (if any).
 * This component is purely visual. */
export const Form: React.FC<FormProps> = ({
  disabled = false,
  errorMessage = '',
  fields,
  handleSubmit,
  renderAfterFields,
  submitText = 'Submit',
  containerStyle,
  buttonProps = {},
}) => {
  return (
    <form onSubmit={handleSubmit} style={containerStyle}>
      <FieldsContainer>
        {fields.map(field => {
          /** Remove fields that don't belong on `Input` component  */
          const isCustom = Boolean(field.customInput);
          const inputFields = { ...field };
          delete inputFields.customInput;
          delete inputFields.label;
          delete inputFields.errorMessage;

          return (
            <InputContainer key={field.label}>
              <InputLabel htmlFor={field.label}>{field.label}</InputLabel>
              {isCustom ? (
                field.customInput
              ) : (
                <Input {...inputFields} id={field.label} />
              )}
              {field.errorMessage && (
                <ErrorMessage>{field.errorMessage}</ErrorMessage>
              )}
            </InputContainer>
          );
        })}
      </FieldsContainer>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      {renderAfterFields || <></>}
      <Centered>
        <Button {...buttonProps} type="submit" disabled={disabled}>
          {submitText}
        </Button>
      </Centered>
    </form>
  );
};

export default Form;
